<template>
  <div class="page home">
    <div class="content">
      <div class="left-panel">
        <div
          v-if="user.organization"
          class="group left-panel-button"
          :class="{ active: workspace === workspaceTypes.organization }"
          @click="getGroupResumes(workspaceTypes.organization)"
        >
          <p class="ellipsis">{{ user.organization.name }}</p>
          <span class="total-group-resumes">{{ metrics.organizationCount }}</span>
        </div>
        <hr v-if="user.organization" class="group-separator" />
        <div
          class="group left-panel-button"
          :class="{ active: workspace === workspaceTypes.personal }"
          @click="getGroupResumes(workspaceTypes.personal)"
        >
          <p>{{ $t('dashboard.workspaceGroup.personal') }}</p>
          <span class="total-group-resumes">{{ metrics.personalCount }}</span>
        </div>
        <hr v-if="metrics.sharedCount" class="group-separator" />
        <div
          v-if="metrics.sharedCount"
          class="group left-panel-button"
          :class="{ active: workspace === workspaceTypes.shared }"
          @click="getGroupResumes(workspaceTypes.shared)"
        >
          <p>{{ $t('dashboard.workspaceGroup.shared') }}</p>
          <span class="total-group-resumes">{{ metrics.sharedCount }}</span>
        </div>

        <div class="new-folder-button left-panel-button button-create" @click.stop="createResume">
          <Add class="add-button" />{{ $t('dashboard.createButtons.resume') }}
        </div>
      </div>
      <div class="resumes">
        <div class="list-header">
          <h1 v-if="workspace === workspaceTypes.personal">
            {{ $t('dashboard.workspaceGroup.personal') }}
          </h1>
          <h1 v-if="workspace === workspaceTypes.shared">
            {{ $t('dashboard.workspaceGroup.shared') }}
          </h1>
          <div v-if="workspace === workspaceTypes.search" class="search-result-account">
            <h1>{{ $t('dashboard.searchResult.title') }}</h1>
            <span>
              {{ searchResult.total
              }}{{
                searchResult.total > 1 ? $t('dashboard.searchResult.plural') : $t('dashboard.searchResult.singular')
              }}
            </span>
          </div>
          <div v-if="workspace === workspaceTypes.organization" class="group-info">
            <img
              v-if="user.organization.logoLink"
              class="organization-logo"
              :src="user.organization.logoLink"
              alt="organization logo"
            />
            <div class="title-edit">
              <div class="organization-title">
                <h1 class="ellipsis">
                  {{ user.organization.name }}
                </h1>
                <div class="shared-popin-container">
                  <Men
                    class="organization-member-icon"
                    @mouseover="getOrganizationMembers()"
                    @mouseleave="
                      isSharedIcon = null;
                      sharedInformation = { collabos: {} };
                      fetch = false;
                    "
                  />
                  <ShareTooltip
                    v-if="isSharedIcon === 'organization' && sharedInformation.collabos.length"
                    :organization-members="true"
                    :shared-information="sharedInformation"
                    :collaborators="collaborators"
                  />
                </div>
              </div>
              <router-link :to="{ name: 'editOrganisation' }" class="link-hover">
                <Pen class="pen-icon" />
                <p>{{ $t('dashboard.workspaceGroup.edit') }}</p>
              </router-link>
            </div>
          </div>
          <div v-if="userResumes.length" class="search-bar" :class="{ active: searchActive }">
            <Glass class="magnifying-glass" />
            <input
              v-model="search"
              type="text"
              :placeholder="$t('dashboard.searchResult.inputPlaceholder')"
              @focus="() => (searchActive = true)"
              @blur="() => (searchActive = false)"
            />
            <transition name="fade">
              <Notification
                v-if="notification.message"
                class="notification"
                :html-message="notification.message"
                :is-success="notification.success"
              />
            </transition>
          </div>
        </div>
        <div v-if="isFetching('resumes')" class="loader">
          <Loader class="loader-icon" />
        </div>
        <SearchResult
          v-show="search.length && !isFetching('resumes')"
          :current-page="pageNumber"
          :sort="sort"
          :collaborators="collaborators"
          :search="search"
          @sort-by="sortBy"
          @share="share"
          @reload="reload"
          @scroll="scrollOnSearch"
        />
        <ResumesList
          v-if="!search.length && userResumes.length && !isFetching('resumes')"
          :sort="sort"
          :workspace="workspace"
          :collaborators="collaborators"
          @sort-by="sortBy"
          @share="share"
          @reload="reload"
          @scroll="scroll"
        />
        <div v-if="!userResumes.length && !isFetching('resumes') && emptyContainer" class="empty-group">
          <p>{{ $t('dashboard.emptyGroup.sentence') }}</p>
          <a href="#create" class="nomobile" @click.stop="createResume">{{ $t('dashboard.emptyGroup.link') }}</a>
          <a href="#create" class="mobile" v-html="$t('dashboard.emptyGroup.linkMobile')"></a>
        </div>
        <Pagination
          v-if="!isFetching('resume') && ((userResumes.length && !search) || searchResult.total)"
          :current-page="pageNumber"
          :total-pages="totalPages"
          @go-to-page="pagination"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import initFireBase from '@/FireBase';

import SearchResult from '@/components/dashboard/SearchResult';
import ResumesList from '@/components/dashboard/ResumesList';
import Notification from '@/components/Notification';
import Pagination from '@/components/dashboard/Pagination';
import ShareTooltip from '@/components/dashboard/ShareTooltip';

import Add from '@/assets/listing/add.svg';
import Pen from '@/assets/listing/pen.svg';
import Men from '@/assets/listing/mens.svg';
import Glass from '@/assets/listing/magnifying glass.svg';
import Loader from '@/assets/listing/loader Washi.svg';
import enums from '@/helpers/enums';

export default {
  name: 'DashboardView',
  components: {
    SearchResult,
    ResumesList,
    Add,
    Pen,
    Men,
    Glass,
    Loader,
    Notification,
    Pagination,
    ShareTooltip,
  },
  data() {
    return {
      workspace: this.$route.query.workspace ? Number(this.$route.query.workspace) : undefined,
      workspaceTypes: enums.workspaceTypes,
      emptyContainer: false,
      pageNumber: this.$route.query.pageNumber ? Number(this.$route.query.pageNumber) - 1 : 0,
      sort: '-UpdatedAt',
      search: '',
      searchActive: false,
      isSharedIcon: false,
      sharedInformation: { collabos: {} },
    };
  },
  computed: {
    ...mapGetters([
      'resumes',
      'metrics',
      'user',
      'isFetching',
      'searchResult',
      'emailNotification',
      'notification',
      'resetSearch',
    ]),
    userResumes() {
      return this.resumes.items;
    },
    collaborators() {
      return this.sharedInformation.collabos.length < 5 ? this.sharedInformation.collabos : [];
    },
    mainGroup() {
      if (!this.user.organization || (this.metrics.organizationCount === 0 && this.metrics.personalCount > 1))
        return this.workspaceTypes.personal;
      else if (this.metrics.sharedCount && (!this.metrics.organizationCount || !this.metrics.personalCount))
        return this.workspaceTypes.shared;
      else return this.workspaceTypes.organization;
    },
    totalPages() {
      return this.workspace === this.workspaceTypes.search
        ? Math.ceil(this.searchResult.total / 15)
        : Math.ceil(this.resumes.total / 15);
    },
  },
  watch: {
    search() {
      if (!this.search.length && this.workspace === this.workspaceTypes.search) {
        this.getMainGroup();
      } else if (this.search.length) {
        this.workspace = 0;
        this.pageNumber = 0;
      }
    },
    resetSearch() {
      this.search = '';
    },
  },
  async mounted() {
    try {
      initFireBase(this.$store);
    } catch (e) {
      console.error(e);
    }
    const action = localStorage.getItem('action');
    const token = localStorage.getItem('token');
    if (action && token) {
      await this.triggerAction(action, token);
    }
    this.getMainGroup();
  },
  methods: {
    scroll(container) {
      let bottomOfResumesList = container.scrollTop + container.clientHeight >= container.scrollHeight - 128;
      if (bottomOfResumesList && !this.isFetching('scroll-resumes') && this.resumes.offSet !== this.resumes.total) {
        this.pageNumber += 1;
        this.$store.dispatch('resumes.scroll', {
          workspace: this.workspace,
          pageNumber: this.pageNumber,
          sortBy: this.sort,
        });
      }
    },
    scrollOnSearch(container) {
      let bottomOfResumesList = container.scrollTop + container.clientHeight >= container.scrollHeight - 128;
      if (
        bottomOfResumesList &&
        !this.isFetching('scroll-resumes') &&
        this.searchResult.offSet !== this.searchResult.total
      ) {
        this.pageNumber += 1;
        this.$store.dispatch('search.scroll', {
          search: this.search,
          pageNumber: this.pageNumber,
        });
      }
    },
    async sortBy(sort) {
      this.sort = sort;
      this.pageNumber = 0;
      await this.$store.dispatch('resumes.fetch', {
        workspace: this.workspace,
        pageNumber: 0,
        sortBy: sort,
      });
    },
    async reload() {
      await this.getMainGroup();
    },
    async triggerAction(action, token) {
      switch (action) {
        case 'sharing':
          await this.$store.dispatch(`sharing.get`, token);
          break;
        default:
          console.error('action unknown');
      }
      localStorage.removeItem('action');
      localStorage.removeItem('token');
    },
    async getMainGroup() {
      const result = await this.$store.dispatch('resumes.metrics');
      if (result) {
        if (!this.workspace) {
          if ((!this.user.organization && result.personalCount) || (!result.organizationCount && result.personalCount))
            this.workspace = this.workspaceTypes.personal;
          else if (result.sharedCount && !result.organizationCount && !result.personalCount)
            this.workspace = this.workspaceTypes.shared;
          else if (!result.sharedCount && !result.organizationCount && !result.personalCount) {
            this.workspace = this.workspaceTypes.personal;
            this.emptyContainer = true;
          } else this.workspace = this.workspaceTypes.organization;
        }
        this.$store.dispatch('resumes.fetch', {
          workspace: this.workspace,
          pageNumber: this.pageNumber,
          sortBy: this.sort,
        });
      }
    },
    async getGroupResumes(workspace) {
      this.$router.push({ query: { workspace, pageNumber: 1 } });
      this.workspace = workspace;
      this.pageNumber = 0;
      this.sort = '-UpdatedAt';
      this.search = '';
      await this.$store.dispatch(`resumes.fetch`, {
        workspace: this.workspace,
        pageNumber: this.pageNumber,
        sortBy: this.sort,
      });
      !this.resumes.items.length ? (this.emptyContainer = true) : (this.emptyContainer = false);
    },
    async createResume() {
      const result = await this.$store.dispatch('setPopup', {
        popupType: 'CreateResumePopup',
      });
      if (result) {
        this.$router.push({ name: 'editor', params: { id: result.id } });
      }
    },
    async share(resume) {
      this.editMenu = null;
      try {
        const result = await this.$store.dispatch('setPopup', {
          popupType: 'ShareResume',
          popupData: resume,
        });
        if (result === true) {
          const htmlMessage = this.$t('dashboard.notification.added', {
            email: this.emailNotification,
          });
          const notification = {
            message: htmlMessage,
            success: true,
          };
          this.$store.dispatch('sendNotification', notification);
          this.reload();
        } else {
          if (!result) return;
          else if (result.code === 400009) {
            const htmlMessage = this.$t('dashboard.notification.alreadyHaveAccess', {
              email: this.emailNotification,
              organization: this.user.organization.name,
            });
            const notification = {
              message: htmlMessage,
              success: false,
            };
            this.$store.dispatch('sendNotification', notification);
          } else {
            const htmlMessage = this.$t('dashboard.notification.cantShare', {
              email: this.emailNotification,
            });
            const notification = {
              message: htmlMessage,
              success: false,
            };
            this.$store.dispatch('sendNotification', notification);
          }
        }
      } catch (e) {
        console.error(e);
      }
    },
    async getOrganizationMembers() {
      if (this.fetch === false) {
        this.fetch = true;
        this.isSharedIcon = 'organization';
        const result = await this.$store.dispatch('organization.fetch');
        this.sharedInformation.collabos = result.users;
      }
    },
    async pagination(page) {
      this.pageNumber = page;
      this.$router.push({
        query: { workspace: this.workspace, pageNumber: page + 1 },
      });
      if (this.workspace === this.workspaceTypes.search) {
        await this.$store.dispatch('resume.search', {
          search: this.search,
          pageNumber: this.pageNumber,
        });
      } else {
        await this.$store.dispatch('resumes.fetch', {
          workspace: this.workspace,
          pageNumber: this.pageNumber,
          sortBy: this.sort,
        });
      }
    },
  },
};
</script>
<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/buttons.scss';
@use '@/styles/responsive/dashboard.scss';

$layout-breakpoint-small: 512px;

.page {
  padding: 96px 0 0;
  .content {
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    position: relative;
  }
  .left-panel {
    width: 350px;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    .left-panel-button {
      display: flex;
      align-items: center;
      width: 240px;
      height: 30px;
      border-radius: 5px;
      padding: 0 10px;
      font-family: Lato, sans-serif;
      font-weight: bold;
      line-height: 17px;
      cursor: pointer;
    }
    .group {
      position: relative;
      justify-content: flex-start;
      background-color: colors.$white-color;
      font-size: 14px;
      margin: 5px;
      color: colors.$dark-grey-color;
      .total-group-resumes {
        text-align: right;
        margin-left: auto;
      }
      p {
        width: 230px;
      }
      &.active {
        background-color: colors.$secondary-color;
        color: colors.$white-color;
      }
      &:first-child {
        margin-top: 0;
      }
      &:not(.active):hover {
        background-color: colors.$hover-background-color;
      }
    }
    .new-folder-button {
      justify-content: center;
      font-size: 11px;
      text-transform: uppercase;
      background-color: colors.$light-blue-color;
      color: colors.$white-color;
      margin-top: 80px;

      .add-button {
        color: colors.$light-blue-color;
        margin-right: 10px;
      }
    }

    .group-separator {
      width: 260px;
      border: none;
      border-top: 1px solid #d0d0d0;
    }
  }
  .resumes {
    flex-grow: 1;
    padding-right: 64px;
    .list-header {
      height: 53px;
      max-width: 1000px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 10px;
      h1 {
        font-family: Lato, sans-serif;
        font-size: 24px;
        font-weight: 700;
        margin: 0;
      }
      .search-result-account {
        display: flex;
        flex-direction: column;
      }
      .group-info {
        display: flex;
        align-items: flex-start;
        width: 60%;
        .organization-logo {
          height: 55px;
          width: 55px;
          border-radius: 5px;
        }
        .title-edit {
          font-family: Lato, sans-serif;
          margin-left: 10px;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          width: 100%;
          .organization-title {
            cursor: default;
            position: relative;
            font-size: 24px;
            height: 27px;
            display: flex;
            align-items: center;
            width: 100%;
            .shared-popin-container {
              position: relative;
              height: 100%;
            }
            .organization-member-icon {
              position: relative;
              top: -8px;
              left: -3px;
              height: 180%;
              &:hover {
                color: colors.$light-blue-color;
              }
            }
            h1.ellipsis {
              max-width: calc(100% - 50px);
            }
            h1:not(.ellipsis) {
              width: 100%;
            }
          }
          a {
            cursor: pointer;
            position: relative;
            box-sizing: border-box;
            margin-left: 2px;
            height: 20px;
            font-size: 10px;
            font-weight: 700;
            color: colors.$light-blue-color;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            width: 98px;
            transition: all 0.3s ease;
            .pen-icon {
              position: relative;
              top: 0;
              height: 10px;
              width: 10px;
              margin-right: 5px;
            }
            p {
              position: relative;
            }
            &:hover {
              padding-left: 7px;
            }
          }
        }
      }
      .search-bar {
        display: flex;
        box-sizing: border-box;
        align-items: center;
        border-radius: 5px;
        border: solid 1px #d0d0d0;
        width: 30%;
        height: 30px;
        padding-right: 10px;
        color: colors.$light-text-color;
        background: colors.$white-color;
        position: relative;

        .notification {
          right: -1px;
          min-width: 300px;
        }

        input {
          background: none;
          border: none;
          width: 100%;
          outline: none;
          font-size: 12px;
        }
        &.active {
          border: 1px solid colors.$blue-color;
          color: colors.$blue-color;
          box-shadow:
            inset 0 1px 3px 0 colors.$light-blue-color,
            0 1px 3px 0 colors.$blue-color;
        }
        .magnifying-glass {
          margin-right: 10px;
        }
      }
    }
    .loader {
      width: 100%;
      height: 600px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 50px;
      .loader-icon {
        width: 5%;
        animation: rotation 0.5s infinite;
        @-webkit-keyframes rotation {
          from {
            -webkit-transform: rotate(0deg);
          }
          to {
            -webkit-transform: rotate(359deg);
          }
        }
      }
    }
  }
  .empty-group {
    background-color: colors.$white-color;
    height: 200px;
    width: 100%;
    border-radius: 7px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 7px 0 #dddddd;
    p {
      font-size: 18px;
      line-height: 25px;
      font-style: italic;
      font-weight: 300;
    }
    a {
      line-height: 25px;
      font-weight: 700;
      font-size: 14px;
      color: colors.$light-blue-color;
    }
  }
}

@media screen and (max-width: $layout-breakpoint-small) {
  .group-info {
    .title-edit a {
      margin-top: 6px !important;
    }
  }
  .empty-group {
    margin-top: 70px;

    .mobile {
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
    }
  }
}
</style>
